import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { X, Check, XCircle, Loader2, FileSignature, ShieldCheck } from "lucide-react";

// Iter 116 — Admin Change-Request Review dashboard tab. Mirrors the
// existing Contract Review pattern: proofs are presigned via
// /admin/change-requests/{crid}/proofs. Admin enters the verified values
// himself on approve (like settlement) — not blind-accept.
//
// Iter 144 Part 3 — Per-vertical display: each CR row shows a vertical
// tag ("CASINO" or "CASE"). Selected-CR detail renders ONLY that
// vertical's proposed values + admin-typed inputs. Approve body sends
// only the row's own scope. Foreign-vertical fields are neither shown
// nor sent — the backend keys off cr.vertical.

const CATEGORY_KEYS = ["slots", "house_originals", "table_games", "live_casino", "sports", "dice"];

function isImage(ct) { return typeof ct === "string" && ct.startsWith("image/"); }
function isVideo(ct) { return typeof ct === "string" && ct.startsWith("video/"); }

function VerticalTag({ vertical }) {
  const label = vertical === "casino" ? "CASINO" : vertical === "case_opening" ? "CASE" : "LEGACY";
  const tone = vertical === "casino"
    ? "border-[#F5B841]/50 text-[#F5B841]"
    : vertical === "case_opening"
      ? "border-[#2DD4A7]/50 text-[#2DD4A7]"
      : "border-white/20 text-[#8B97AE]";
  return (
    <span data-testid={`admin-cr-vertical-${vertical || "legacy"}`}
      className={`inline-block px-2 py-0.5 border font-mono text-[9px] uppercase tracking-widest ${tone}`}>{label}</span>
  );
}

function ReviewModal({ cr, onClose, onActed }) {
  const [proofs, setProofs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const vertical = cr.vertical; // "casino" | "case_opening" | undefined (legacy)

  // Iter 144 — Only initialize state for the row's own vertical. Foreign
  // fields are never rendered nor sent.
  const [cats, setCats] = useState(() => vertical === "casino"
    ? Object.fromEntries(CATEGORY_KEYS.map((k) => [k, Number(cr.proposedCategories?.[k] || 0)]))
    : Object.fromEntries(CATEGORY_KEYS.map((k) => [k, 0])));
  const [caseWager, setCaseWager] = useState(vertical === "case_opening" ? Number(cr.proposedCaseWager || 0) : 0);
  const [offCasino, setOffCasino] = useState(vertical === "casino" ? Number(cr.proposedOfferedVolumeByVertical?.casino || 0) : 0);
  const [offCase, setOffCase] = useState(vertical === "case_opening" ? Number(cr.proposedOfferedVolumeByVertical?.case_opening || 0) : 0);
  const [note, setNote] = useState("");
  // Current player values fetched on open — enables "current → proposed" display.
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/admin/change-requests/${cr.id}/proofs`)
      .then(({ data }) => { if (!cancelled) setProofs(data); })
      .catch((e) => { if (!cancelled) toast.error(apiErrorMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    // Best-effort: fetch player detail so admin sees "current → proposed".
    // Soft-fails silently — proposed values remain visible either way.
    api.get(`/players/${cr.playerId}`)
      .then(({ data }) => { if (!cancelled) setCurrent(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [cr.id, cr.playerId]);

  const approve = async () => {
    setBusy("approve");
    try {
      // Iter 144 Part 3 — Send only the scope's own fields. Backend
      // requires the row's vertical fields positive; foreign fields are
      // silently ignored server-side but we omit them for clarity.
      const body = { note };
      if (vertical === "casino") {
        body.approvedCategories = cats;
        body.approvedOfferedVolumeByVertical = { casino: offCasino || 0 };
      } else if (vertical === "case_opening") {
        body.approvedCaseWager = caseWager;
        body.approvedOfferedVolumeByVertical = { case_opening: offCase || 0 };
      } else {
        // Legacy CR (no vertical) — cannot approve via scoped path.
        toast.error("This change request predates per-vertical scoping. Please reject with a note asking the player to resubmit.");
        setBusy(null);
        return;
      }
      await api.post(`/admin/change-requests/${cr.id}/approve`, body);
      toast.success("Change request approved — player values updated");
      onActed && onActed();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === "object" && d.message) || apiErrorMessage(e));
    }
    finally { setBusy(null); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) { toast.error("Reason is required"); return; }
    setBusy("reject");
    try {
      await api.post(`/admin/change-requests/${cr.id}/reject`, { reason: rejectReason.trim() });
      toast.success("Rejected — player notified");
      onActed && onActed();
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(null); }
  };

  const currentCats = current?.wagerCategoriesSelfReported || {};
  const currentCasinoOffered = Number(current?.offeredVolumeByVertical?.casino || 0);
  const currentCaseOffered   = Number(current?.offeredVolumeByVertical?.case_opening || 0);
  const currentCaseWager     = Number(current?.caseWagerSelfReported || 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-cr-modal">
      <div className="w-full max-w-3xl bg-[#0B1A14] border border-[#F5B841]/40 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 sticky top-0 bg-[#0B1A14]">
          <div className="flex items-center gap-2">
            <FileSignature className="w-4 h-4 text-[#F5B841]" />
            <h3 className="font-display font-bold text-base text-[#E8ECF4]">Change request · {cr.player_alias}</h3>
            <VerticalTag vertical={vertical} />
          </div>
          <button onClick={onClose} className="text-[#8B97AE] hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-5">
          <div className="border border-white/10 p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] mb-2" data-testid="admin-cr-proposed-header">
              Player proposed — {vertical === "casino" ? "casino" : vertical === "case_opening" ? "case" : "legacy"}
            </div>

            {vertical === "casino" && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs font-mono" data-testid="admin-cr-proposed-cats">
                  {CATEGORY_KEYS.map((k) => {
                    const prop = Number(cr.proposedCategories?.[k] || 0);
                    const cur  = Number(currentCats?.[k] || 0);
                    return (
                      <div key={k} className="flex justify-between border-b border-white/5 py-1">
                        <span className="text-[#8B97AE]">{k.replace(/_/g, " ")}</span>
                        <span><span className="text-[#8B97AE]">{cur.toLocaleString()}</span> → <span className="text-[#F5B841]">{prop.toLocaleString()}</span></span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 text-xs font-mono" data-testid="admin-cr-proposed-offered-casino">
                  Casino offered: <span className="text-[#8B97AE]">{currentCasinoOffered.toLocaleString()}</span> → <span className="text-[#F5B841]">{Number(cr.proposedOfferedVolumeByVertical?.casino || 0).toLocaleString()}</span>
                </div>
              </>
            )}

            {vertical === "case_opening" && (
              <>
                <div className="text-xs font-mono" data-testid="admin-cr-proposed-case-wager">
                  Case wager basis: <span className="text-[#8B97AE]">{currentCaseWager.toLocaleString()}</span> → <span className="text-[#F5B841]">{Number(cr.proposedCaseWager || 0).toLocaleString()}</span>
                </div>
                <div className="mt-1 text-xs font-mono" data-testid="admin-cr-proposed-offered-case">
                  Case offered: <span className="text-[#8B97AE]">{currentCaseOffered.toLocaleString()}</span> → <span className="text-[#F5B841]">{Number(cr.proposedOfferedVolumeByVertical?.case_opening || 0).toLocaleString()}</span>
                </div>
              </>
            )}

            {!vertical && (
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#F26D6D]" data-testid="admin-cr-legacy-warn">
                Legacy CR — no vertical scope. Reject with a note asking the player to resubmit under the new per-vertical panels.
              </div>
            )}

            {cr.note && <div className="mt-2 text-[11px] text-[#8B97AE]">Player note: {cr.note}</div>}
          </div>

          <div className="border border-white/10 p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7] mb-2">
              <ShieldCheck className="w-3 h-3" />Fresh proof screenshots
            </div>
            {loading ? (
              <div className="flex items-center gap-1 text-[10px] text-[#8B97AE]"><Loader2 className="w-3 h-3 animate-spin" />Loading proofs…</div>
            ) : proofs?.files?.length ? (
              <ul className="space-y-2" data-testid="admin-cr-proof-list">
                {proofs.files.map((f) => (
                  <li key={f.id} className="border border-white/10 p-2 space-y-1">
                    <div className="flex items-center justify-between text-[11px] font-mono">
                      <span className="text-[#E8ECF4] truncate mr-2">{f.filename}</span>
                      {f.url && <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-[10px] uppercase tracking-widest text-[#F5B841] hover:text-white">Open</a>}
                    </div>
                    {f.url && isImage(f.contentType) && <img src={f.url} alt="" className="max-h-64 object-contain border border-white/10" />}
                    {f.url && isVideo(f.contentType) && <video src={f.url} controls className="max-h-64 w-full border border-white/10" />}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#F26D6D]">— no proofs recorded —</div>
            )}
          </div>

          {vertical && (
            <div className="border border-[#2DD4A7]/40 p-3 bg-[#2DD4A7]/[0.04]">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7] mb-2">Enter verified values (admin-typed — not blind-accept)</div>
              {vertical === "casino" && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                    {CATEGORY_KEYS.map((k) => (
                      <div key={k}>
                        <label className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE] block mb-1">{k.replace(/_/g, " ")}</label>
                        <input data-testid={`admin-cr-cat-${k}`} type="text" inputMode="numeric"
                          value={cats[k] ? Number(cats[k]).toLocaleString() : ""}
                          onChange={(e) => setCats({ ...cats, [k]: Number(e.target.value.replace(/[^\d]/g, "")) || 0 })}
                          className="w-full rounded-none bg-transparent border border-white/20 h-9 font-mono tabular-nums text-right text-[#E8ECF4] px-2 text-xs" />
                      </div>
                    ))}
                  </div>
                  <div>
                    <label className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE] block mb-1">Casino offered</label>
                    <input data-testid="admin-cr-off-casino" type="text" inputMode="numeric"
                      value={offCasino ? Number(offCasino).toLocaleString() : ""}
                      onChange={(e) => setOffCasino(Number(e.target.value.replace(/[^\d]/g, "")) || 0)}
                      className="w-full md:w-1/2 rounded-none bg-transparent border border-white/20 h-9 font-mono tabular-nums text-right text-[#E8ECF4] px-2 text-xs" />
                  </div>
                </>
              )}
              {vertical === "case_opening" && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE] block mb-1">Case wager basis</label>
                    <input data-testid="admin-cr-case-wager" type="text" inputMode="numeric"
                      value={caseWager ? Number(caseWager).toLocaleString() : ""}
                      onChange={(e) => setCaseWager(Number(e.target.value.replace(/[^\d]/g, "")) || 0)}
                      className="w-full rounded-none bg-transparent border border-white/20 h-9 font-mono tabular-nums text-right text-[#E8ECF4] px-2 text-xs" />
                  </div>
                  <div>
                    <label className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE] block mb-1">Case offered</label>
                    <input data-testid="admin-cr-off-case" type="text" inputMode="numeric"
                      value={offCase ? Number(offCase).toLocaleString() : ""}
                      onChange={(e) => setOffCase(Number(e.target.value.replace(/[^\d]/g, "")) || 0)}
                      className="w-full rounded-none bg-transparent border border-white/20 h-9 font-mono tabular-nums text-right text-[#E8ECF4] px-2 text-xs" />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="pt-2 border-t border-white/10 flex flex-wrap gap-2">
            <button data-testid="admin-cr-approve" onClick={approve} disabled={busy !== null || !vertical}
              className="inline-flex items-center gap-1.5 px-4 h-9 bg-[#2DD4A7] text-[#04150F] text-xs font-mono uppercase tracking-widest hover:bg-[#F5B841] transition-colors disabled:opacity-40">
              <Check className="w-3 h-3" />{busy === "approve" ? "…" : "Approve — apply values"}
            </button>
            <button data-testid="admin-cr-reject-open" onClick={() => setRejectOpen(!rejectOpen)} disabled={busy !== null}
              className="inline-flex items-center gap-1.5 px-4 h-9 border border-[#F26D6D]/60 text-[#F26D6D] text-xs font-mono uppercase tracking-widest hover:bg-[#F26D6D]/[0.08]">
              <XCircle className="w-3 h-3" />Reject…
            </button>
          </div>
          {rejectOpen && (
            <div className="border border-[#F26D6D]/40 bg-[#F26D6D]/[0.06] p-3 space-y-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#F26D6D]">Reason (required)</div>
              <textarea data-testid="admin-cr-reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value.slice(0, 1000))}
                rows={3} placeholder="Explain what's wrong so the player can resubmit…"
                className="w-full bg-transparent border border-white/20 text-xs text-[#E8ECF4] p-2 font-mono outline-none focus:border-[#F26D6D]" />
              <div className="flex justify-end gap-2">
                <button onClick={() => { setRejectOpen(false); setRejectReason(""); }} className="px-3 h-8 border border-white/20 text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] hover:text-white">Cancel</button>
                <button data-testid="admin-cr-reject-confirm" onClick={reject} disabled={busy !== null || !rejectReason.trim()}
                  className="px-3 h-8 bg-[#F26D6D] text-white text-[10px] font-mono uppercase tracking-widest hover:opacity-90 disabled:opacity-40">{busy === "reject" ? "…" : "Reject & notify"}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminChangeRequests({ onAction } = {}) {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/change-requests");
      setRows(data || []);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleActed = useCallback(() => { load(); onAction && onAction(); }, [load, onAction]);
  const openRow = openId ? rows.find((r) => r.id === openId) : null;

  const proposedOfferedForRow = (r) => {
    if (r.vertical === "casino")       return Number(r.proposedOfferedVolumeByVertical?.casino || 0);
    if (r.vertical === "case_opening") return Number(r.proposedOfferedVolumeByVertical?.case_opening || 0);
    return 0;
  };

  return (
    <section data-testid="admin-change-requests" className="space-y-4">
      {loading ? (
        <div className="text-xs text-[#8B97AE] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-[#8B97AE] border border-white/10 p-6 text-center">No pending change requests.</div>
      ) : (
        <div className="border border-white/10">
          <table className="w-full text-sm">
            <thead className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
              <tr className="border-b border-white/10 bg-white/[0.02]">
                <th className="text-left p-2">Player</th>
                <th className="text-left p-2">Vertical</th>
                <th className="text-right p-2">Proposed offered</th>
                <th className="text-left p-2">Proofs</th>
                <th className="text-left p-2">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`admin-cr-row-${r.id}`} onClick={() => setOpenId(r.id)}
                    className="border-b border-white/5 last:border-b-0 cursor-pointer hover:bg-white/[0.03]">
                  <td className="p-2 font-mono text-xs">{r.player_alias}</td>
                  <td className="p-2"><VerticalTag vertical={r.vertical} /></td>
                  <td className="p-2 font-mono text-xs text-right text-[#F5B841]">{proposedOfferedForRow(r).toLocaleString()}</td>
                  <td className="p-2 font-mono text-xs">{(r.proofFileIds || []).length}</td>
                  <td className="p-2 font-mono text-xs text-[#8B97AE]">{(r.submittedAt || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {openRow && <ReviewModal cr={openRow} onClose={() => setOpenId(null)} onActed={handleActed} />}
    </section>
  );
}
